import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Sparkles } from "lucide-react";
import { SERVICES, BRAND } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service_interest: "",
  preferred_date: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. Your note arrived safely — I'll be in touch soon.");
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went sideways. Please try again or email me directly.";
      toast.error(typeof msg === "string" ? msg : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36 px-6 sm:px-10 lg:px-16 bg-[#070707]"
    >
      <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left — copy */}
        <div className="lg:col-span-5">
          <div className="text-xs font-sans tracking-[0.22em] uppercase text-zinc-500 overline-line">
            Begin the Conversation
          </div>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05] text-white">
            Let's see if <br />
            <span className="italic text-rainbow">we're a fit.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-md">
            Share a little about where you are and what you're seeking. I read every
            note personally and reply within 48 hours with a thoughtful next step.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-full bg-brand-elevated border border-brand-border flex items-center justify-center">
                <Mail size={16} className="text-brand-gold" strokeWidth={1.5} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Email</div>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="text-white font-serif text-lg hover:text-brand-amber transition"
                  data-testid="contact-info-email"
                >
                  {BRAND.email}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-full bg-brand-elevated border border-brand-border flex items-center justify-center">
                <MapPin size={16} className="text-brand-gold" strokeWidth={1.5} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Where I work</div>
                <div className="text-white font-serif text-lg">Worldwide, virtually</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-full bg-brand-elevated border border-brand-border flex items-center justify-center">
                <Sparkles size={16} className="text-brand-gold" strokeWidth={1.5} />
              </span>
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">Response</div>
                <div className="text-white font-serif text-lg">Within 48 hours</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-brand-surface border border-brand-border p-8 sm:p-10 rounded-3xl"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Your name" required>
              <input
                data-testid="contact-input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Maya Rivers"
                className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg"
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@youremail.com"
                className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg"
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="(555) 123-4567"
                className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg"
              />
            </Field>
            <Field label="What are you drawn to?">
              <select
                data-testid="contact-input-service"
                value={form.service_interest}
                onChange={set("service_interest")}
                className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg appearance-none cursor-pointer"
              >
                <option value="" className="bg-black">— Select —</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.name} className="bg-black">
                    {s.name}
                  </option>
                ))}
                <option value="Podcast Guest Inquiry" className="bg-black">
                  Podcast Guest Inquiry
                </option>
                <option value="Just saying hello" className="bg-black">
                  Just saying hello
                </option>
              </select>
            </Field>
            <Field label="Preferred date (optional)" className="sm:col-span-2">
              <input
                data-testid="contact-input-date"
                type="date"
                value={form.preferred_date}
                onChange={set("preferred_date")}
                className="input-dark w-full py-3 px-0 border-0 border-b font-serif text-lg [color-scheme:dark]"
              />
            </Field>
            <Field label="Tell me what's on your heart" required className="sm:col-span-2">
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={set("message")}
                rows={5}
                placeholder="Where are you right now? What are you longing for?"
                className="input-dark w-full py-3 px-0 border-0 border-b font-sans text-base leading-relaxed resize-none"
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
              Your words are held with care. I'll never share your information.
            </p>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-amber text-black font-semibold hover:bg-brand-amberHover transition disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_40px_-12px_rgba(230,126,34,0.55)]"
            >
              {submitting ? "Sending…" : "Send your note"}
              <Send size={16} strokeWidth={1.75} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
        {label}
        {required && <span className="text-brand-amber ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
